import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Send, Shield, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGOS = {
  orange: "/images/logos/orange.svg"
};

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Vul alle velden in');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Bericht verzonden! We nemen zo snel mogelijk contact met u op.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Contact submission error:', error);
      toast.error('Er ging iets mis. Probeer opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Same style as HomePage */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="text-xl font-bold text-blue-600">Telecomprijsvergelijker<span className="text-orange-500">.be</span></div>
            </Link>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-full">
                <img src={LOGOS.orange} alt="Orange" className="h-5" />
                <span className="text-xs font-medium text-orange-700">Officieel Partner</span>
              </div>
              <Button onClick={() => navigate('/')} variant="outline" size="sm">
                Terug naar home
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-4 py-12">
        {/* Contact Form Card */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Contactformulier</h1>
              <p className="text-slate-600">
                Heeft u een vraag? Of wenst u uw gegevens in te kijken of te verwijderen? Stuur ons gerust een bericht.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name">Naam *</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={isSubmitting}
                  className="mt-1"
                  data-testid="contact-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="email">E-mailadres *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={isSubmitting}
                  className="mt-1"
                  data-testid="contact-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="message">Bericht *</Label>
                <Textarea
                  id="message"
                  rows={5}
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  disabled={isSubmitting}
                  placeholder="Hoe kunnen we u helpen?"
                  className="mt-1"
                  data-testid="contact-message-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700" 
                disabled={isSubmitting}
                data-testid="contact-submit-button"
              >
                {isSubmitting ? 'Bezig...' : (
                  <>Bericht versturen <Send className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </form>

            {/* Privacy note */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 text-slate-600 text-sm">
                <Shield className="w-4 h-4 text-green-600" />
                <span>Uw gegevens worden enkel gebruikt om uw vraag te beantwoorden.</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Footer - Same style as HomePage */}
      <footer className="bg-slate-900 text-slate-400 py-12 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-xl font-bold text-white mb-4">
                Telecomprijsvergelijker<span className="text-orange-500">.be</span>
              </div>
              <p className="text-sm mb-4">
                Wij helpen u besparen op TV, internet en mobiel door Telenet, Proximus en Base te vergelijken met Orange's aanbod.
              </p>
              <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-2 inline-flex">
                <img src={LOGOS.orange} alt="Orange" className="h-6" />
                <span className="text-sm text-white font-medium">Officieel Partner</span>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacybeleid</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookiebeleid</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <span className="flex items-center gap-2 text-white">
                    <Phone className="w-4 h-4" />
                    Contactformulier
                  </span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
            <p className="mb-2">Officieel Orange partner via distributiekanaal SmartSN</p>
            <p>© {new Date().getFullYear()} Telecomprijsvergelijker.be - Alle rechten voorbehouden</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
